import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateMap } from '~/api/projects2';
import type { MapDetail, MapStyle } from '~/common/types';
import { civilDrawMapQueryKey } from './useCivilDrawMap';

/** Saves a CivilDraw map's basemap, changing the cached map before the save. */
export function useUpdateCivilDrawMapBaseMap(projectId: string, mapId: string) {
  const queryClient = useQueryClient();
  const queryKey = civilDrawMapQueryKey(projectId, mapId);

  const mutation = useMutation({
    mutationFn: ({ map }: { map: MapDetail; previousBaseMap: MapStyle }) => {
      // The map's objects aren't part of its settings, so they stay out of the request
      const { objects: _objects, ...settings } = map;
      return updateMap(projectId, settings);
    },
    onError: (_error, { previousBaseMap }) => {
      queryClient.setQueryData<MapDetail>(queryKey, (map) => map && { ...map, baseMap: previousBaseMap });
    },
  });

  return (baseMap: MapStyle) => {
    const map = queryClient.getQueryData<MapDetail>(queryKey);
    if (!map || map.baseMap === baseMap) return;
    const updatedMap = { ...map, baseMap };
    queryClient.setQueryData<MapDetail>(queryKey, updatedMap);
    mutation.mutate({ map: updatedMap, previousBaseMap: map.baseMap });
  };
}
